import axios from 'axios'

import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { withSessionRecovery } from '@/services/session-recovery'
import type { ApiResponse, PaginatedResponse } from '@/types'

export interface PostStatusChoice {
  value: number
  label: string
}

export interface AssignedRolePost {
  id: string
  status?: number | string
  created_on?: string | null
  user_name?: string | null
  user_data?: {
    id?: string
    name?: string | null
  }
  roles_name?: string | null
  roles_data?: {
    id?: string
    roles_name?: string | null
    role_level?: number | null
    role_level_code?: string | null
    role_level_label?: string | null
    permissions?: Record<string, unknown> | null
    unique_id?: string | null
  }
}

type AssignedRolePayload =
  | AssignedRolePost[]
  | PaginatedResponse<AssignedRolePost>
  | {
      data?: AssignedRolePost[]
      results?: AssignedRolePost[]
      count?: number
    }

const emptyPage = <T>(): PaginatedResponse<T> => ({
  count: 0,
  next: null,
  previous: null,
  results: [],
})

const extractPayload = <T>(payload: ApiResponse<T> | T): T => (
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? payload.data
    : payload
) as T

const normalizePaginatedResponse = <T>(
  payload: T[] | PaginatedResponse<T> | { data?: T[]; results?: T[] } | undefined,
): PaginatedResponse<T> => {
  if (!payload) {
    return emptyPage<T>()
  }

  if (Array.isArray(payload)) {
    return {
      count: payload.length,
      next: null,
      previous: null,
      results: payload,
    }
  }

  if ('count' in payload && Array.isArray(payload.results)) {
    return payload
  }

  const results = Array.isArray(payload.results)
    ? payload.results
    : 'data' in payload && Array.isArray(payload.data)
      ? payload.data
      : []

  return {
    count: results.length,
    next: null,
    previous: null,
    results,
  }
}

const fetchAssignedRolesPage = async ({
  orgInstanceId,
  serviceInstanceId,
  page,
  pageSize,
  includeHidden,
  status,
  search,
}: {
  orgInstanceId: string
  serviceInstanceId: string
  page: number
  pageSize: number
  includeHidden?: boolean
  status?: Array<number | string>
  search?: string
}): Promise<PaginatedResponse<AssignedRolePost>> => {
  const response = await api.get<ApiResponse<AssignedRolePayload> | AssignedRolePayload>(API_PATHS.posts.list, {
    params: {
      org_instance_id: orgInstanceId,
      service_instance_id: serviceInstanceId,
      page,
      page_size: pageSize,
      include_hidden: includeHidden ? 1 : undefined,
      status: status && status.length > 0 ? status.join(',') : undefined,
      search: search || undefined,
    },
  })

  return normalizePaginatedResponse(extractPayload(response.data))
}

export const postsApi = {
  getAssignedRolesPage: async ({
    orgInstanceId,
    serviceInstanceId,
    page = 1,
    pageSize = 10,
    includeHidden = false,
    status,
    search,
  }: {
    orgInstanceId: string
    serviceInstanceId: string
    page?: number
    pageSize?: number
    includeHidden?: boolean
    status?: Array<number | string>
    search?: string
  }): Promise<PaginatedResponse<AssignedRolePost>> => {
    try {
      return await withSessionRecovery(() => fetchAssignedRolesPage({ orgInstanceId, serviceInstanceId, page, pageSize, includeHidden, status, search }))
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 424) {
        return emptyPage<AssignedRolePost>()
      }

      throw error
    }
  },
  updateAssignmentStatus: async (id: string, status: number | string): Promise<void> => {
    await withSessionRecovery(() => api.patch(API_PATHS.posts.update(id), { status }))
  },
  getStatusChoices: async (): Promise<PostStatusChoice[]> => {
    const response = await withSessionRecovery(() =>
      api.get<ApiResponse<PostStatusChoice[]>>(API_PATHS.posts.statusChoices)
    )
    const res = response.data as ApiResponse<PostStatusChoice[]>
    return Array.isArray(res?.data) ? res.data : (res as unknown as PostStatusChoice[])
  },
}